// Helpers for handling external video URLs (YouTube, Vimeo, Google Drive, plain links).
// Pure functions — no DOM, no network. Safe to use anywhere.

const YT_HOSTS = new Set([
  'youtube.com',
  'www.youtube.com',
  'm.youtube.com',
  'music.youtube.com',
  'youtu.be',
  'www.youtu.be',
]);

const VIMEO_HOSTS = new Set(['vimeo.com', 'www.vimeo.com', 'player.vimeo.com']);

const DRIVE_HOSTS = new Set([
  'drive.google.com',
  'docs.google.com',
]);

function safeParseUrl(raw) {
  if (!raw || typeof raw !== 'string') return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;
  // Reject anything that already specifies a non-http(s) protocol (ftp://, file://, etc.).
  if (/^[a-z][a-z0-9+.-]*:/i.test(trimmed) && !/^https?:\/\//i.test(trimmed)) {
    return null;
  }
  try {
    // Default to https:// if user pasted "youtube.com/..." without protocol.
    const withProto = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
    const u = new URL(withProto);
    if (u.protocol !== 'http:' && u.protocol !== 'https:') return null;
    return u;
  } catch {
    return null;
  }
}

export function parseVideoUrl(raw) {
  const u = safeParseUrl(raw);
  if (!u) return null;

  const host = u.hostname.toLowerCase();

  // --- YouTube ---
  if (YT_HOSTS.has(host)) {
    let id = null;

    if (host === 'youtu.be' || host === 'www.youtu.be') {
      // youtu.be/<id>
      id = u.pathname.split('/').filter(Boolean)[0] || null;
    } else if (u.pathname === '/watch') {
      id = u.searchParams.get('v');
    } else if (u.pathname.startsWith('/embed/')) {
      id = u.pathname.slice('/embed/'.length).split('/')[0] || null;
    } else if (u.pathname.startsWith('/shorts/')) {
      id = u.pathname.slice('/shorts/'.length).split('/')[0] || null;
    } else if (u.pathname.startsWith('/live/')) {
      id = u.pathname.slice('/live/'.length).split('/')[0] || null;
    }

    if (!id || !/^[\w-]{6,20}$/.test(id)) return null;

    return {
      provider: 'youtube',
      id,
      url: `https://www.youtube.com/watch?v=${id}`,
      embedUrl: `https://www.youtube.com/embed/${id}`,
      thumbnailUrl: `https://img.youtube.com/vi/${id}/hqdefault.jpg`,
    };
  }

  // --- Vimeo ---
  if (VIMEO_HOSTS.has(host)) {
    const parts = u.pathname.split('/').filter(Boolean);
    // vimeo.com/<id> or player.vimeo.com/video/<id>
    let id = null;
    if (parts[0] === 'video' && parts[1]) id = parts[1];
    else if (parts[0] && /^\d+$/.test(parts[0])) id = parts[0];

    if (!id || !/^\d{5,15}$/.test(id)) return null;

    return {
      provider: 'vimeo',
      id,
      url: `https://vimeo.com/${id}`,
      embedUrl: `https://player.vimeo.com/video/${id}`,
      thumbnailUrl: null, // Vimeo thumbs need an API call; skip for now.
    };
  }

  // --- Google Drive ---
  // Accepted formats:
  //   https://drive.google.com/file/d/<id>/view?usp=sharing
  //   https://drive.google.com/file/d/<id>/preview
  //   https://drive.google.com/open?id=<id>
  //   https://drive.google.com/uc?id=<id>&export=download
  //   https://docs.google.com/file/d/<id>/edit
  // The file MUST be shared as "Anyone with the link → Viewer" on Drive for
  // anyone other than the uploader to view it.
  if (DRIVE_HOSTS.has(host)) {
    let id = null;
    const parts = u.pathname.split('/').filter(Boolean);
    const dIdx = parts.indexOf('d');
    if (dIdx >= 0 && parts[dIdx + 1]) {
      id = parts[dIdx + 1];
    } else if (u.pathname === '/open' || u.pathname === '/uc') {
      id = u.searchParams.get('id');
    }

    if (!id || !/^[\w-]{10,}$/.test(id)) return null;

    return {
      provider: 'drive',
      id,
      url: `https://drive.google.com/file/d/${id}/view`,
      embedUrl: `https://drive.google.com/file/d/${id}/preview`,
      thumbnailUrl: `https://drive.google.com/thumbnail?id=${id}&sz=w320`,
    };
  }

  // --- Generic video URL ---
  // Accept https links that look like a direct video file.
  if (u.protocol === 'https:' || u.protocol === 'http:') {
    const looksLikeVideoFile = /\.(mp4|webm|mov|m4v|ogv)(\?|#|$)/i.test(u.pathname);
    if (looksLikeVideoFile) {
      return {
        provider: 'direct',
        id: null,
        url: u.toString(),
        embedUrl: u.toString(),
        thumbnailUrl: null,
      };
    }
  }

  return null;
}

export function isSupportedVideoUrl(raw) {
  return parseVideoUrl(raw) !== null;
}
